import { typedQueryClient } from "hooks";
import { WarrantyQueryKeys } from "modules/WarrantyDashboard/queryKeys";

import type { WebSocketMessageListener } from "components";
import type { Appointment, Intervention, Pin } from "models";

const interventionListeners: WebSocketMessageListener<Intervention>[] = [
  {
    model: "Intervention",
    action: "upsert",
    callback: ({ data: intervention }) => {
      const filters = typedQueryClient.getQueryData(WarrantyQueryKeys.dashboardFilters);
      if (!filters) return;

      const paginatedPins = typedQueryClient.getQueryData(WarrantyQueryKeys.listWarranties);
      if (!paginatedPins?.items?.length) return;

      const interventionIdx = paginatedPins.items.findIndex(i => i.intervention_id === intervention.id);
      if (interventionIdx < 0) return;

      const pin = paginatedPins.items[interventionIdx];
      typedQueryClient.setQueryData(WarrantyQueryKeys.listWarranties, {
        ...paginatedPins,
        items: paginatedPins.items.with(interventionIdx, { ...pin, intervention: { ...pin.intervention, ...intervention } })
      });
    }
  }
];

const appointmentListeners: WebSocketMessageListener<Appointment>[] = [
  {
    model: "Appointment",
    action: "upsert",
    callback: ({ data: appointment }) => {
      const filters = typedQueryClient.getQueryData(WarrantyQueryKeys.dashboardFilters);
      if (!filters) return;

      const warrantyData = typedQueryClient.getQueryData(WarrantyQueryKeys.listWarranties);
      if (!warrantyData?.items?.length) return;

      const appointmentIdx = warrantyData.items.findIndex(i => i.appointment_id === appointment.id);
      if (appointmentIdx < 0) return;

      const pin = warrantyData.items[appointmentIdx];
      typedQueryClient.setQueryData(WarrantyQueryKeys.listWarranties, {
        ...warrantyData,
        items: warrantyData.items.with(appointmentIdx, { ...pin, appointment: { ...pin.appointment, ...appointment } })
      });
    }
  }
];

const pinListeners: WebSocketMessageListener<Pin>[] = [
  {
    model: "Pin",
    action: "upsert",
    callback: ({ data: pin }) => {
      const paginatedPins = typedQueryClient.getQueryData(WarrantyQueryKeys.listWarranties);
      if (!paginatedPins) return;

      let pinIdx =
        paginatedPins.items?.findIndex(
          i => (i.question_result_id && i.question_result_id === pin.question_result_id) || (i.intervention_id && i.intervention_id === pin.intervention_id)
        ) ?? -1;

      const updatedPins = {
        ...paginatedPins,
        items: !paginatedPins.items || pinIdx < 0 ? [pin, ...(paginatedPins.items ?? [])] : paginatedPins.items.with(pinIdx, { ...paginatedPins.items[pinIdx], ...pin }),
        nb_items: !paginatedPins.items || pinIdx < 0 ? paginatedPins.nb_items + 1 : paginatedPins.nb_items
      };

      typedQueryClient.setQueryData(WarrantyQueryKeys.listWarranties, updatedPins);

      const pinHistory = typedQueryClient.getQueryData(WarrantyQueryKeys.pinHistory);
      if (
        !pinHistory?.length ||
        (pin.intervention_id && pin.intervention_id !== pinHistory[0]?.intervention_id) ||
        (pin.question_result_id && pin.question_result_id !== pinHistory[0]?.question_result_id)
      )
        return;

      pinIdx = pinHistory.findIndex(p => p.id === pin.id);
      typedQueryClient.setQueryData(WarrantyQueryKeys.pinHistory, pinIdx < 0 ? [pin, ...pinHistory] : pinHistory.with(pinIdx, { ...pinHistory[pinIdx], ...pin }));
    }
  },

  {
    model: "Pin",
    action: "delete",
    callback: ({ data: pin }) => {
      const paginatedPins = typedQueryClient.getQueryData(WarrantyQueryKeys.listWarranties);
      if (!paginatedPins?.items?.length) return;

      const pinIdx = paginatedPins.items.findIndex(p => p.id === pin.id);
      typedQueryClient.setQueryData(WarrantyQueryKeys.listWarranties, {
        ...paginatedPins,
        items: paginatedPins.items.toSpliced(pinIdx, 1),
        nb_items: paginatedPins.nb_items - 1
      });
    }
  }
];

export const useWarrantyDashboardListeners = () => [...interventionListeners, ...appointmentListeners, ...pinListeners];
